import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { demoActAs, getServices } from '@infrastructure/services';
import {
  DEMO_ADMIN,
  DEMO_FINANZAS,
  DEMO_GLOBAL,
  DEMO_SUPERUSER,
  P_ANA,
  P_MARCO,
} from '@infrastructure/demo/seed';
import type { Role } from '@domain/vocabulary';
import { cx } from '../design-system';
import { useSessionState } from '../providers/SessionProvider';
import styles from './AppShell.module.css';

/**
 * Role switcher, demo mode only.
 *
 * The four roles present four materially different applications — different
 * navigation, different screens, different controls on the same screen — and
 * without a backend there is no other way to review them. It renders nothing
 * outside demo mode, and `demoActAs` throws there, because impersonating a
 * colleague is precisely what the real auth path must never allow.
 */

const PEOPLE: readonly { id: string; label: string; role: Role }[] = [
  { id: P_MARCO, label: 'Colaborador', role: 'empleado' },
  { id: P_ANA, label: 'Líder', role: 'lider' },
  { id: DEMO_ADMIN.id, label: 'Admin', role: 'admin' },
  { id: DEMO_FINANZAS.id, label: 'Finanzas', role: 'finanzas' },
  { id: DEMO_GLOBAL.id, label: 'Global', role: 'global' },
  { id: DEMO_SUPERUSER.id, label: 'Super', role: 'super' },
];

export const DemoRoleSwitcher = (): ReactNode => {
  const { status } = useSessionState();
  const navigate = useNavigate();

  if (!getServices().isDemo) return null;

  const currentId = status.kind === 'ready' ? status.session.personId : null;

  return (
    <div className={styles.roleTrack} role="group" aria-label="Cambiar de rol en modo demo">
      {PEOPLE.map((person) => (
        <button
          key={person.id}
          type="button"
          aria-pressed={currentId === person.id}
          className={cx(styles.roleOption, currentId === person.id && styles.roleOptionActive)}
          onClick={() => {
            demoActAs(person.id as never);
            void navigate(
              person.role === 'lider' ? '/aprobaciones' : person.role === 'admin' ? '/administrador' : '/',
            );
          }}
        >
          {person.label}
        </button>
      ))}
    </div>
  );
};
